// Página de registro de nuevos usuarios
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import { User, Mail, Lock, UserPlus, ArrowLeft } from 'lucide-react'
import useAuth from '../hooks/useAuth'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import { useQuery } from '@tanstack/react-query'
import client from '../api/client'

export default function Register() {
  const { handleRegister, isRegistering, registerError } = useAuth()
  const { register, handleSubmit, watch, formState: { errors } } = useForm()
  const password = watch('password')

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })

  const logoSistema = settings?.logo_sistema || '/favicon.svg'
  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')
  const logoUrl = logoSistema.startsWith('/uploads') ? `${baseImgUrl}${logoSistema}` : (logoSistema.startsWith('/') ? `${window.__APP_BASE_URL__ || '/'}${logoSistema.substring(1)}` : logoSistema)

  const onSubmit = (data) => {
    handleRegister({
      name: data.name,
      email: data.email,
      password: data.password,
      password_confirmation: data.confirmPassword,
    })
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 16px',
        background: 'radial-gradient(ellipse at top, rgba(0, 194, 255, 0.12) 0%, transparent 60%), linear-gradient(180deg, #0A1F44 0%, #0D1B2A 100%)',
        position: 'relative',
      }}
    >
      {/* Patrón de fondo sutil */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.15,
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: '420px',
        }}
      >
        {/* Logo y título */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Link to="/" style={{ display: 'inline-block', marginBottom: '16px' }} className="group">
            <img
              src={logoUrl}
              alt="Logo"
              style={{
                height: '48px',
                width: 'auto',
                maxWidth: '140px',
                objectFit: 'contain',
                margin: '0 auto',
              }}
              className="transition-transform duration-700 ease-out group-hover:rotate-[360deg] group-hover:scale-110"
            />
          </Link>
          <h1
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 700,
              fontSize: '1.75rem',
              color: '#F1F5F9',
              margin: '0 0 4px 0',
            }}
          >
            Crea tu cuenta
          </h1>
          <p style={{ fontSize: '0.875rem', color: '#94A3B8', margin: 0 }}>
            Únete y arma tu predicción del Mundial 2026
          </p>
        </div>

        {/* Formulario */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.07)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '32px',
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Error global */}
            {registerError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  fontSize: '0.875rem',
                  color: '#EF4444',
                }}
              >
                {registerError}
              </motion.div>
            )}

            <Input
              label="Nombre completo"
              type="text"
              icon={User}
              placeholder="Juan Pérez"
              error={errors.name?.message}
              {...register('name', {
                required: 'El nombre es obligatorio',
                minLength: {
                  value: 2,
                  message: 'Mínimo 2 caracteres',
                },
              })}
            />

            <Input
              label="Correo electrónico"
              type="email"
              icon={Mail}
              placeholder="tu@correo.com"
              error={errors.email?.message}
              {...register('email', {
                required: 'El correo es obligatorio',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Correo electrónico no válido',
                },
              })}
            />

            <Input
              label="Contraseña"
              type="password"
              icon={Lock}
              placeholder="Mínimo 8 caracteres"
              error={errors.password?.message}
              {...register('password', {
                required: 'La contraseña es obligatoria',
                minLength: {
                  value: 8,
                  message: 'Mínimo 8 caracteres',
                },
              })}
            />

            <Input
              label="Confirmar contraseña"
              type="password"
              icon={Lock}
              placeholder="Repite tu contraseña"
              error={errors.confirmPassword?.message}
              {...register('confirmPassword', {
                required: 'Confirma tu contraseña',
                validate: (value) =>
                  value === password || 'Las contraseñas no coinciden',
              })}
            />

            <Button
              type="submit"
              size="lg"
              loading={isRegistering}
              icon={UserPlus}
              style={{ width: '100%', marginTop: '8px' }}
            >
              Crear cuenta
            </Button>

            <p style={{ fontSize: '0.75rem', color: '#64748B', textAlign: 'center', margin: 0 }}>
              Al registrarte, aceptas nuestros términos de uso y política de privacidad.
            </p>
          </form>

          {/* Separador */}
          <div style={{ position: 'relative', margin: '24px 0' }}>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '100%', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }} />
            </div>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
              <span style={{ padding: '0 12px', background: 'rgba(13, 27, 42, 0.8)', color: '#64748B', fontSize: '0.75rem' }}>
                o
              </span>
            </div>
          </div>

          {/* Enlace a login */}
          <p style={{ textAlign: 'center', fontSize: '0.875rem', color: '#94A3B8', margin: 0 }}>
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" style={{ color: '#00C2FF', fontWeight: 500, textDecoration: 'none' }}>
              Inicia sesión
            </Link>
          </p>

          {/* Botón volver a la página principal */}
          <div style={{ marginTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '16px', textAlign: 'center' }}>
            <Link to="/" style={{ color: '#94A3B8', fontSize: '0.8125rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', transition: 'color 0.2s' }} onMouseEnter={(e) => e.target.style.color = '#00C2FF'} onMouseLeave={(e) => e.target.style.color = '#94A3B8'}>
              <ArrowLeft style={{ width: '14px', height: '14px' }} /> Volver a la página principal
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
